import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowDown, ArrowUp, Coins } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "@/i18n/LanguageContext";

type Transaction = {
  id: number;
  userId: number;
  recipientId: number | null;
  amount: string;
  transactionType: "PUZZLE_CREATION" | "PRIZE_INCREASE" | "HINT_PURCHASE" | "PUZZLE_SOLVED";
  referenceId: number | null;
  referenceType: string | null;
  description: string;
  balanceAfter: string;
  createdAt: string;
  recipient?: {
    displayName: string;
  };
};

const getTransactionTypeLabel = (type: string, t: any) => {
  const types: Record<string, string> = {
    "PUZZLE_CREATION": t("cacatais_transactions.puzzleCreation"),
    "PRIZE_INCREASE": t("cacatais_transactions.prizeIncrease"),
    "HINT_PURCHASE": t("cacatais_transactions.hintPurchase"),
    "PUZZLE_SOLVED": t("cacatais_transactions.puzzleSolved"),
  };
  
  return types[type] || type;
};

const getTransactionTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    "PUZZLE_CREATION": "bg-yellow-100 text-yellow-800",
    "PRIZE_INCREASE": "bg-yellow-100 text-yellow-800",
    "HINT_PURCHASE": "bg-red-100 text-red-800",
    "PUZZLE_SOLVED": "bg-green-100 text-green-800",
  };
  
  return colors[type] || "bg-gray-100 text-gray-800";
};

export default function CacataisTransactionsPage() {
  const [, setLocation] = useLocation();
  const { t } = useTranslation();

  const { data: user } = useQuery({
    queryKey: ["/api/auth/user"],
  });

  const { data: transactions, isLoading } = useQuery<Transaction[]>({
    queryKey: ["/api/user/cacatais-transactions"],
    enabled: !!user,
  });

  // Redirect to home if not authenticated
  useEffect(() => {
    if (user === null) {
      setLocation("/");
    }
  }, [user, setLocation]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              {t("cacatais_transactions.title")}
            </CardTitle>
            <CardDescription>
              {t("cacatais_transactions.pageDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-10">{t("cacatais_transactions.loading")}</div>
            ) : transactions?.length === 0 ? (
              <div className="text-center py-10">{t("cacatais_transactions.noTransactionsFound")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("cacatais_transactions.date")}</TableHead>
                    <TableHead>{t("cacatais_transactions.type")}</TableHead>
                    <TableHead>{t("cacatais_transactions.description")}</TableHead>
                    <TableHead className="text-right">{t("cacatais_transactions.amount")}</TableHead>
                    <TableHead className="text-right">{t("cacatais_transactions.balance")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions?.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(transaction.createdAt), "MMM dd, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Badge className={getTransactionTypeColor(transaction.transactionType)}>
                          {getTransactionTypeLabel(transaction.transactionType, t)}
                        </Badge>
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <span className={`flex items-center justify-end gap-1 ${parseFloat(transaction.amount) < 0 ? "text-red-600" : "text-green-600"}`}>
                          {parseFloat(transaction.amount) < 0 ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                          {Math.abs(parseFloat(transaction.amount))} {t("cacatais_transactions.cacatais")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {transaction.balanceAfter} {t("cacatais_transactions.cacatais")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
